import { DollarSign, Percent, Hash, Users, Calendar, Clock, Zap } from 'lucide-react';
import { CustomAdventureInput } from '@/utils/businessAppApi';
import { PricingType, CurrencyOption } from './types';

export const DEFAULT_OPTIONS: CustomAdventureInput['options'] = [
  { 
    label: 'Group Size (Number of People)', 
    type: 'number', 
    required: true,
    helpText: '🏗️ Foundation Field: This determines how your base price scales and controls all per-person add-ons (meals, accommodation, etc.)',
    placeholder: 'e.g. 4',
    isFoundationField: true,
    businessCategory: 'pricing-foundation'
  },

  { 
    label: 'Meals Included', 
    type: 'checkbox', 
    required: false,
    helpText: 'Optional add-on: Customers can choose to include meals in their package',
    businessCategory: 'add-ons'
  },
  { 
    label: 'Professional Guide', 
    type: 'checkbox', 
    required: false,
    helpText: 'Optional add-on: Customers can choose to add a professional guide to their adventure',
    businessCategory: 'add-ons'
  },
  { 
    label: 'Vehicle Type', 
    type: 'select', 
    required: false, 
    options: ['Safari Jeep (+$20/day)', 'Premium 4WD (+$40/day)', 'Luxury Safari Vehicle (+$60/day)'],
    helpText: 'Choice with pricing: Let customers choose their preferred vehicle type with different pricing tiers',
    businessCategory: 'upgrades'
  },
];

export const PRICING_TYPES: PricingType[] = [
  // 🟢 SIMPLE PRICING (Most Common)
  {
    value: 'fixed',
    label: 'Fixed Amount',
    businessLabel: 'Fixed Price',
    icon: DollarSign,
    category: 'simple',
    description: 'Add a fixed amount to the total price',
    example: 'Equipment rental: +$50 (always the same)',
    helpText: 'This adds the same amount every time, regardless of number of people or days. Perfect for equipment, permits, or one-time fees.',
    isRecommended: true
  },
  {
    value: 'percentage',
    label: 'Percentage',
    businessLabel: 'Percentage of Base Price',
    icon: Percent,
    category: 'simple',
    description: 'Add a percentage of the base adventure price',
    example: 'Service fee: +15% (15% of $200 base = +$30)',
    helpText: 'This adds a percentage of your base adventure price. Good for service fees, taxes, or profit margins.',
    isRecommended: false
  },

  // 🟢 PER-PERSON PRICING (Highly Recommended)
  {
    value: 'per_person',
    label: 'Per Person',
    businessLabel: 'Price Per Person',
    icon: Users,
    category: 'per_person',
    description: 'Multiply the price by number of people',
    example: 'Meals: $30 × 4 people = +$120 total',
    helpText: 'This multiplies the price by the number of people. Perfect for meals, accommodation, or any per-person costs. Automatically detects "Number of People" fields.',
    isRecommended: true
  },

  // 🟢 PER-DAY PRICING (Highly Recommended)  
  {
    value: 'per_day',
    label: 'Per Day',
    businessLabel: 'Price Per Day',
    icon: Calendar,
    category: 'per_day',
    description: 'Multiply the price by number of days',
    example: 'Camping: $25 × 3 days = +$75 total',
    helpText: 'This multiplies the price by the number of days. Perfect for accommodation, daily permits, or multi-day costs. Automatically detects "Number of Days" fields.',
    isRecommended: true
  },

  // 🟢 PER-PERSON-DAY PRICING (Most Comprehensive)
  {
    value: 'per_person_day',
    label: 'Per Person Per Day',
    businessLabel: 'Price Per Person Per Day',
    icon: Clock,
    category: 'per_person',
    description: 'Multiply by both people and days',
    example: 'Full board: $40 × 4 people × 3 days = +$480',
    helpText: 'This multiplies by both number of people AND number of days. Perfect for full board meals, daily guide fees, or comprehensive services. Most flexible option.',
    isRecommended: true
  },

  // 🟡 LEGACY/ADVANCED PRICING (For Advanced Users)
  {
    value: 'flat',
    label: 'Flat Fee (Legacy)',
    businessLabel: 'Legacy Fixed Fee',
    icon: DollarSign,
    category: 'advanced',
    description: 'Legacy flat fee - use Fixed Amount instead',
    example: 'Same as Fixed Amount: +$50',
    helpText: 'Legacy pricing type. Use "Fixed Amount" instead for better clarity.',
    isRecommended: false
  },
  {
    value: 'per_unit',
    label: 'Per Unit (Advanced)',
    businessLabel: 'Advanced Per-Unit Pricing',
    icon: Hash,
    category: 'advanced',
    description: 'Multiply by quantity or custom multiplier',
    example: 'Custom calculation: $10 × quantity',
    helpText: 'Advanced option for custom calculations. Requires manual multiplier setup. Use specific per-person or per-day options instead.',
    isRecommended: false
  }
];

export const CURRENCY_OPTIONS: CurrencyOption[] = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' }
];

// NEW: Base pricing types for scalable pricing
export const BASE_PRICING_TYPES = [
  {
    value: 'fixed' as const,
    label: 'Fixed Price',
    businessLabel: 'Fixed Amount',
    icon: DollarSign,
    category: 'simple',
    description: 'Same price regardless of group size or duration',
    example: 'Safari Tour: $200 total (always the same)',
    helpText: 'Perfect for experiences where the cost doesn\'t depend on group size, like private vehicle tours or equipment rentals.',
    isRecommended: false
  },
  {
    value: 'per_person' as const,
    label: 'Per Person',
    businessLabel: 'Price Per Person',
    icon: Users,
    category: 'per_person',
    description: 'Price multiplied by number of people',
    example: 'Wildlife Walk: $50 × 4 people = $200 total',
    helpText: 'Most common pricing model. Perfect for guided tours, meals, accommodation, or any activity where costs scale with group size.',
    isRecommended: true
  },
  {
    value: 'per_day' as const,
    label: 'Per Day',
    businessLabel: 'Price Per Day',
    icon: Calendar,
    category: 'per_day',
    description: 'Price multiplied by number of days',
    example: 'Camping Site: $75 × 3 days = $225 total',
    helpText: 'Ideal for accommodation, multi-day permits, or equipment rentals where duration affects cost.',
    isRecommended: true
  },
  {
    value: 'per_person_day' as const,
    label: 'Per Person Per Day',
    businessLabel: 'Price Per Person Per Day',
    icon: Zap,
    category: 'advanced',
    description: 'Price multiplied by people AND days',
    example: 'Full Package: $30 × 4 people × 3 days = $360 total',
    helpText: 'Ultimate flexibility for comprehensive packages where both group size and duration matter. Perfect for all-inclusive adventures.',
    isRecommended: true
  }
] as const; 